import { useParams } from 'react-router';

import { IconCertificate } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import { IconButton, Tooltip } from '@cfa/react-core';
import { InfoOutline } from '@cfa/system-icons';

import useAuth from '@/hooks/useAuth';
import useGetXPSummaryReportByUser from '@/hooks/useGetXPSummaryReportByUser';

import { getCompletionStatusText } from '@/utils/getCompletionStatusText';
import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';
import {
  getHrsFromTotalMins,
  getMinsFromTotalMins,
  getMinsFromTotalSeconds,
  getTimeSpentOnPlanForTeamMember,
} from '@/utils/time';
import { useRouteMatcher } from '@/utils/url';

import { Header } from '@/components/header';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import AssignmentStatusSummary from '@/components/PlanCards/AssignmentStatusSummary';
import PlanItemCard from '@/components/PlanCards/PlanItemCard';
import PlanItemProcedure from '@/components/PlanCards/PlanItemProcedure';
import PlanItemQuiz from '@/components/PlanCards/PlanItemQuiz';
import PlanItemTask from '@/components/PlanCards/PlanItemTask';
import {
  PrintCertificateModals,
  usePrintCertificates,
} from '@/components/PrintCertificates';

import Constants, { routes } from '@/constants';

import {
  AssignmentLatestCompletionReport,
  getPlanAndAssignmentsOptions,
  getUsersForLocationsOptions,
} from '@/_generated/api-clients/xp';
import { userPrefsAtom } from '@/atoms/userPrefs';

const TeamMemberProgressView: React.FC = () => {
  const { planId, teamMemberId } = useParams<{
    planId: string;
    teamMemberId: string;
  }>();
  const safePlanId = planId ?? '';
  const safeTeamMemberId = teamMemberId ?? '';
  const { t } = useTranslation();
  const routeMatches = useRouteMatcher();
  const { certificatesEpic } = useFlags();
  const { data: auth } = useAuth();
  const locationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  const isReportsView = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsPath,
  );
  const isReportsPlansPath = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsPlansPath,
  );
  const isReportsTeamMembersPath = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsTeamMembersPath,
  );

  const {
    data: plan,
    isFetching: isFetchingPlan,
    isPending: isPendingPlan,
  } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId: safePlanId } }),
    enabled: !!planId && isValidPlanId(planId),
  });

  // XP API get users in Training Plans View
  const {
    data: users,
    isFetching: isFetchingUsersForPlans,
    isPending: isPendingUsersForPlans,
  } = useQuery({
    ...getUsersForLocationsOptions({
      query: { locationNumbers: locationsWithAtLeastTrainer },
    }),
    select: data =>
      data.map(user => ({
        ...user,
        name: user.prefName
          ? `${user.prefName} ${user.lastName}`
          : `${user.firstName} ${user.lastName}`,
      })),
  });

  // New XP report plans by user. Provide user name here.
  const {
    xpReportsData: xpUserReportsData,
    isFetchingXpReport: isFetchingXpUserReport,
  } = useGetXPSummaryReportByUser(teamMemberId);

  const user = !isReportsView
    ? users?.find(planUser => planUser.adId === teamMemberId)
    : xpUserReportsData?.user;

  const userPlanStatus = plan?.assignments?.find(
    status => status.userId === teamMemberId,
  );
  const previousAssignmentData = userPlanStatus?.latestCompletionReport;
  const previousAssignmentCompletionDate =
    previousAssignmentData?.completionDate ?? userPlanStatus?.completionDate;
  const currentAssignmentIsCompleted =
    userPlanStatus?.status === Constants.TRAINING_PLANS.COMPLETED;

  const isStepCompleted = (stepId: string) =>
    userPlanStatus?.steps?.find(step => step.stepId === stepId)?.status ===
    Constants.TRAINING_PLANS.COMPLETE;

  const header = itemByLanguage(plan?.plan?.name);
  const name = user?.name;
  const userId = user?.adId;
  const timeSpentOnPlan = getTimeSpentOnPlanForTeamMember(userPlanStatus);
  const timeSpentHrs = getHrsFromTotalMins(timeSpentOnPlan);
  const timeSpentMins = getMinsFromTotalMins(timeSpentOnPlan);

  const previousAssignmentTimeSpentSeconds =
    previousAssignmentData?.totalDurationInSeconds ?? 0;
  const previousAssignmentTimeSpentMins = getMinsFromTotalSeconds(
    previousAssignmentTimeSpentSeconds,
  );
  const previousAssignmentTimeSpentHrs = getHrsFromTotalMins(
    previousAssignmentTimeSpentMins,
  );

  const completedStatusText = userPlanStatus
    ? getCompletionStatusText({
        timeSpentHrs,
        timeSpentMins,
        completionDate:
          userPlanStatus.latestCompletionReport?.completionDate ??
          userPlanStatus.completionDate,
        // TODO: expirationDate property doesn't exist yet on AssignmentLatestCompletionReport - stubbed for future use
        expirationDate: (
          userPlanStatus.latestCompletionReport as
            | (AssignmentLatestCompletionReport & {
                expirationDate?: string;
              })
            | undefined
        )?.expirationDate,
        // These two props can be removed once the certificates epic is fully rolled out
        certificatesEpicEnabled: certificatesEpic,
        planType: plan?.plan?.type,
      })
    : '';

  const { language: userLanguage } = useAtomValue(userPrefsAtom);

  const certificateHook = usePrintCertificates({ planId, teamMemberId });

  const previousUrl =
    isReportsPlansPath && planId
      ? routes.reports.planProgress({ planId })
      : isReportsTeamMembersPath
        ? routes.reports.teamMemberProgress({
            teamMemberId: safeTeamMemberId,
          })
        : routes.plans.managePlan({
            planId: safePlanId,
          });

  const previousLabel = isReportsPlansPath
    ? planId
      ? header
      : t('Generic.teamMembers')
    : isReportsTeamMembersPath
      ? name
      : header;

  const isFetching =
    isFetchingPlan ||
    isPendingPlan ||
    isFetchingUsersForPlans ||
    isPendingUsersForPlans ||
    isFetchingXpUserReport;

  return (
    <>
      <LoadingOverlay isOpen={isFetching} fixed />
      <Header.Root>
        {previousLabel && (
          <Header.Back to={previousUrl}>{previousLabel}</Header.Back>
        )}
        <Header.Title>{header}</Header.Title>
        {certificateHook.shouldShow && (
          <Header.ActionIconButton
            data-testid="certificate-icon-button"
            icon={IconCertificate}
            onClick={certificateHook.handleClick}
            tooltip="Button.getCertificates"
            disabled={certificateHook.isLoading}
          />
        )}
      </Header.Root>
      {!isFetching && plan?.plan?.content?.steps && (
        <div className="centered-content md:mt-header-height-with-content-spacing px-4 max-md:mt-6">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-variant-h3 text-left">{header}</h3>
            {/* Mobile action button - desktop uses Header.ActionIconButton */}
            {certificateHook.shouldShow && (
              <IconButton
                className="p-0! md:hidden"
                color="secondary"
                onPress={certificateHook.handleClick}
                isDisabled={certificateHook.isLoading}
                data-testid="certificate-button-mobile"
              >
                <IconCertificate width={24} height={24} />
              </IconButton>
            )}
          </div>
          <div className="flex items-center">
            <p>{name}</p>
            <Tooltip.Root delay={250}>
              <IconButton className="ml-2! p-0!" size="sm">
                <InfoOutline />
              </IconButton>
              <Tooltip.Content>
                {t('TrainingPlans.timeSpentOnPlanTooltip')}
              </Tooltip.Content>
            </Tooltip.Root>
          </div>
          <div>
            <AssignmentStatusSummary
              completedStatusText={completedStatusText}
              isCurrentAssignmentCompleted={currentAssignmentIsCompleted}
              previousAssignmentCompletionDate={
                previousAssignmentCompletionDate
              }
              previousAssignmentTimeSpentHrs={previousAssignmentTimeSpentHrs}
              previousAssignmentTimeSpentMins={previousAssignmentTimeSpentMins}
              timeSpentHrs={timeSpentHrs}
              timeSpentMins={timeSpentMins}
            />
          </div>

          <div>
            {plan.plan.content.steps.map((step, index) => {
              switch (step.type) {
                case Constants.STEP_TYPES.QUIZ:
                  return (
                    <PlanItemCard disabled key={index}>
                      <PlanItemQuiz
                        disabled
                        isActive={false}
                        isComplete={isStepCompleted(step.id)}
                        language={userLanguage}
                        onClick={() => {}}
                        onRefetch={() => {}}
                        planId={plan.plan?.id ?? ''}
                        statuses={plan.assignments ?? []}
                        step={step}
                        users={[safeTeamMemberId]}
                      />
                    </PlanItemCard>
                  );
                case Constants.STEP_TYPES.TASK:
                  return (
                    <PlanItemCard disabled key={index}>
                      <PlanItemTask
                        disabled
                        isActive={false}
                        isComplete={isStepCompleted(step.id)}
                        onClick={() => {}}
                        onCompleteToggle={() => {}}
                        planId={plan.plan?.id ?? ''}
                        statuses={plan.assignments ?? []}
                        step={step}
                        userId={userId}
                      />
                    </PlanItemCard>
                  );
                case Constants.STEP_TYPES.DOCUMENT:
                  return (
                    <PlanItemCard disabled key={index}>
                      <PlanItemProcedure
                        disabled
                        isActive={false}
                        isComplete={isStepCompleted(step.id)}
                        isMissing={!step.available}
                        language={userLanguage}
                        onClick={() => {}}
                        onCompleteToggle={() => {}}
                        statuses={plan.assignments ?? []}
                        step={step}
                        userId={userId}
                      />
                    </PlanItemCard>
                  );
                default:
                  return null;
              }
            })}
            {(plan.plan.content.sections ?? []).map(
              section =>
                !!(section.steps ?? []).length && (
                  <PlanItemCard
                    disabled
                    key={section.id}
                    sectionName={itemByLanguage(section.name)}
                  >
                    {section.steps?.map((step, stepIndex) => {
                      switch (step.type) {
                        case Constants.STEP_TYPES.QUIZ:
                          return (
                            <PlanItemQuiz
                              disabled
                              isActive={false}
                              isComplete={isStepCompleted(step.id)}
                              key={stepIndex}
                              language={userLanguage}
                              onClick={() => {}}
                              onRefetch={() => {}}
                              planId={plan.plan?.id ?? ''}
                              statuses={plan.assignments ?? []}
                              step={step}
                              users={[safeTeamMemberId]}
                            />
                          );
                        case Constants.STEP_TYPES.TASK:
                          return (
                            <PlanItemTask
                              disabled
                              isActive={false}
                              isComplete={isStepCompleted(step.id)}
                              key={stepIndex}
                              onClick={() => {}}
                              onCompleteToggle={() => {}}
                              planId={plan.plan?.id ?? ''}
                              statuses={plan.assignments ?? []}
                              step={step}
                              userId={userId}
                            />
                          );
                        case Constants.STEP_TYPES.DOCUMENT:
                          return (
                            <PlanItemProcedure
                              disabled
                              isActive={false}
                              isComplete={isStepCompleted(step.id)}
                              isMissing={!step.available}
                              key={stepIndex}
                              language={userLanguage}
                              onClick={() => {}}
                              onCompleteToggle={() => {}}
                              statuses={plan.assignments ?? []}
                              step={step}
                              userId={userId}
                            />
                          );
                        default:
                          return null;
                      }
                    })}
                  </PlanItemCard>
                ),
            )}
          </div>
        </div>
      )}

      <PrintCertificateModals {...certificateHook} />
    </>
  );
};

export default TeamMemberProgressView;
